import type { JSX } from 'react'
import { Button } from 'primereact/button'

import {
  useVerifyAuditChain,
  type VerifyChainSource,
} from '../../../hooks/useVerifyAuditChain.js'

export interface VerifyChainBadgeProps {
  /**
   * Which audit chain to verify.
   * - `'tenant'` (default) → POST `/admin/compliance/audit-log/verify-chain`
   * - `'platform'` → POST `/platform/audit-log/verify-chain`
   *
   * Forwarded to `useVerifyAuditChain(source)` (UI-8d Task 4).
   */
  source?: VerifyChainSource
}

/**
 * Self-contained verify-chain control for the AuditLogPage header.
 *
 * Owns its own `useVerifyAuditChain` hook — Phase 1 verifies the full
 * chain (no date bounds), matching the plan; a Phase 2 enhancement
 * could plumb the page's date filters through, but the audit module
 * already defaults `from → epoch, to → now` when omitted so a bare
 * `verify()` covers everything.
 *
 * UI-8d Task 4: a `source` prop selects the tenant vs platform chain
 * endpoint; default `'tenant'` preserves the existing UI-7a behaviour.
 *
 * State surfaces:
 *   - idle: "Verify Chain" button.
 *   - verifying: button disabled + spinner.
 *   - valid: green badge "Chain valid".
 *   - broken: red badge with the broken row's id/hash.
 *   - error: red badge with the error message.
 */
export function VerifyChainBadge({
  source = 'tenant',
}: VerifyChainBadgeProps = {}): JSX.Element {
  const { verify, isVerifying, result, error } = useVerifyAuditChain(source)

  return (
    <div className="flex items-center gap-3">
      <Button
        label="Verify Chain"
        icon={isVerifying ? 'pi pi-spin pi-spinner' : 'pi pi-shield'}
        disabled={isVerifying}
        loading={isVerifying}
        onClick={() => {
          void verify()
        }}
      />

      {error && (
        <span
          data-testid="verify-chain-result"
          className="inline-flex items-center rounded-sm bg-red-100 px-2 py-1 text-xs font-medium text-red-800"
        >
          Error: {error}
        </span>
      )}

      {!error && result?.valid && (
        <span
          data-testid="verify-chain-result"
          className="inline-flex items-center rounded-sm bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
        >
          <i className="pi pi-check mr-1" aria-hidden="true" />
          Chain valid
        </span>
      )}

      {!error && result && !result.valid && (
        <span
          data-testid="verify-chain-result"
          className="inline-flex items-center rounded-sm bg-red-100 px-2 py-1 text-xs font-medium text-red-800"
        >
          <i className="pi pi-exclamation-triangle mr-1" aria-hidden="true" />
          Chain broken{result.brokenAt ? ` at ${result.brokenAt}` : ''}
        </span>
      )}
    </div>
  )
}

export default VerifyChainBadge
